import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../lib/i18n";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/mypage";
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    accept_terms: false,
    accept_privacy: false,
    accept_age: false,
    accept_marketing: false, // optional
  });
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const consentReady = form.accept_terms && form.accept_privacy && form.accept_age;

  const submit = async (e) => {
    e.preventDefault();
    if (!consentReady) {
      toast.error(t("auth.consent_required"));
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name || null);
      // Persist marketing preference locally (audit trail).
      try {
        localStorage.setItem(
          "iqlog_consent_v1",
          JSON.stringify({
            terms: true,
            privacy: true,
            age: true,
            marketing: !!form.accept_marketing,
            decided_at: new Date().toISOString(),
          })
        );
      } catch { /* ignore */ }
      toast.success(t("auth.success_register"));
      nav(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16 lg:py-24">
      <div className="mb-10">
        <div className="label-mono mb-2">// Get started</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight uppercase text-white caret">
          {t("auth.register_title")}
        </h1>
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div>
          <label className="block label-mono mb-2">{t("auth.name")}</label>
          <input
            data-testid="register-name-input"
            type="text"
            value={form.name}
            onChange={setField("name")}
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
          />
        </div>
        <div>
          <label className="block label-mono mb-2">{t("auth.email")}</label>
          <input
            data-testid="register-email-input"
            type="email"
            value={form.email}
            onChange={setField("email")}
            required
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
          />
        </div>
        <div>
          <label className="block label-mono mb-2">{t("auth.password")}</label>
          <input
            data-testid="register-password-input"
            type="password"
            value={form.password}
            onChange={setField("password")}
            required
            minLength={6}
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
          />
        </div>

        {/* Consent block */}
        <div className="space-y-3 pt-2 border-t border-[#1E2A3F]">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] pt-3">
            // {t("auth.consent_section")}
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.accept_terms}
              onChange={setField("accept_terms")}
              data-testid="register-accept-terms-checkbox"
              required
              className="mt-1 accent-[#3B82F6]"
            />
            <span className="text-xs text-[#94A3B8] leading-relaxed">
              {t("auth.accept_terms_pre")}{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noreferrer"
                data-testid="register-terms-link"
                className="text-[#3B82F6] hover:underline"
              >
                {t("auth.terms_link")}
              </Link>{" "}
              {t("auth.accept_terms_post")}
              <span className="text-[#EF4444] ml-0.5">*</span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.accept_privacy}
              onChange={setField("accept_privacy")}
              data-testid="register-accept-privacy-checkbox"
              required
              className="mt-1 accent-[#3B82F6]"
            />
            <span className="text-xs text-[#94A3B8] leading-relaxed">
              {t("auth.accept_privacy_pre")}{" "}
              <Link
                to="/privacy"
                target="_blank"
                rel="noreferrer"
                data-testid="register-privacy-link"
                className="text-[#3B82F6] hover:underline"
              >
                {t("auth.privacy_link")}
              </Link>{" "}
              {t("auth.accept_privacy_post")}
              <span className="text-[#EF4444] ml-0.5">*</span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.accept_age}
              onChange={setField("accept_age")}
              data-testid="register-accept-age-checkbox"
              required
              className="mt-1 accent-[#3B82F6]"
            />
            <span className="text-xs text-[#94A3B8] leading-relaxed">
              {t("auth.accept_age")}
              <span className="text-[#EF4444] ml-0.5">*</span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.accept_marketing}
              onChange={setField("accept_marketing")}
              data-testid="register-accept-marketing-checkbox"
              className="mt-1 accent-[#3B82F6]"
            />
            <span className="text-xs text-[#64748B] leading-relaxed">
              {t("auth.accept_marketing")}{" "}
              <span className="text-[#64748B]">({t("auth.optional")})</span>
            </span>
          </label>
        </div>

        <button
          disabled={loading || !consentReady}
          data-testid="register-submit-button"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#3B82F6] text-black font-mono text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] disabled:opacity-40 disabled:cursor-not-allowed neon-glow-soft"
        >
          {loading ? "…" : t("auth.register_btn")}
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </button>
      </form>
      <div className="mt-8 text-sm">
        <Link
          to="/login"
          data-testid="register-goto-login-link"
          className="text-[#3B82F6] hover:underline font-mono text-[11px] uppercase tracking-[0.18em]"
        >
          {t("auth.go_login")} →
        </Link>
      </div>
      <p className="mt-10 text-xs text-[#64748B] leading-relaxed border-t border-[#1E2A3F] pt-6">
        {t("auth.anon_note")}
      </p>
    </div>
  );
}
